import { HttpAgent, Actor } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import { idlFactory } from '../declarations/kip';
import type { _SERVICE, LinkedWallets, WalletLinkChallenge } from '../declarations/kip/kip.did';

import { getCanisterId } from '../config/canisterIds';

// KIP canister service
const getKipCanisterId = (): string => {
  const id = getCanisterId('kip');
  if (!id) {
    throw new Error('KIP canister ID not configured. Set VITE_KIP_CANISTER_ID in .env.production');
  }
  return id;
};

export async function createKipActor(agent: HttpAgent): Promise<_SERVICE> {
  const canisterId = getKipCanisterId();
  
  return Actor.createActor(idlFactory, {
    agent,
    canisterId,
  });
}

export async function createProfile(
  agent: HttpAgent,
  name: string,
  bio?: string,
  preferences?: string
): Promise<string> {
  const actor = await createKipActor(agent);
  return await actor.create_profile(name, bio ? [bio] : [], preferences ? [preferences] : []);
}

export async function getProfile(agent: HttpAgent, principal: Principal) {
  const actor = await createKipActor(agent);
  return await actor.get_profile(principal);
}

export async function updateProfile(
  agent: HttpAgent,
  name: string,
  bio?: string,
  preferences?: string
): Promise<string> {
  const actor = await createKipActor(agent);
  return await actor.update_profile(name, bio ? [bio] : [], preferences ? [preferences] : []);
}

export async function setOisyWallet(agent: HttpAgent, oisyPrincipal: Principal): Promise<string> {
  const actor = await createKipActor(agent);
  return await actor.set_oisy_wallet(oisyPrincipal);
}

export async function getOisyWallet(agent: HttpAgent, principal: Principal): Promise<Principal | null> {
  const actor = await createKipActor(agent);
  const result = await actor.get_oisy_wallet(principal);
  return result.length > 0 ? result[0] : null;
}

export async function startLinkWallet(agent: HttpAgent, kind: string): Promise<WalletLinkChallenge> {
  const actor = await createKipActor(agent);
  return await actor.start_link_wallet(kind);
}

export async function confirmLinkWallet(
  agent: HttpAgent,
  kind: string,
  address: string,
  signature: string,
  nonce: string
): Promise<string> {
  const actor = await createKipActor(agent);
  return await actor.confirm_link_wallet(kind, address, signature, nonce);
}

export async function getLinkedWallets(agent: HttpAgent, principal: Principal): Promise<LinkedWallets | null> {
  const actor = await createKipActor(agent);
  const result = await actor.get_linked_wallets(principal);
  return result.length > 0 ? result[0] : null;
}

export type { LinkedWallets, WalletLinkChallenge };

